// Only angle brackets that open or close one of these tags are HTML. Anything else between angle
// brackets ("Hi <Name>", "Ana <ana@example.com>", "x<y") is text the author meant literally, which an
// HTML parser would otherwise swallow as an unknown element. Those brackets are parked on a private-use
// character while the HTML is parsed, rather than on "&lt;", because happy-dom (the spec environment)
// does not decode every entity. Attributes must carry a value ("="), or a placeholder that happens
// to start with a tag name ("<a time that suits you>") would read as an <a> with boolean attributes.
// An unquoted value follows HTML's rule for one: a run of characters with no whitespace, quote,
// "=" or angle bracket, which is also exactly the run happy-dom reads as the value.
const HTML_TAG_NAMES =
    "a|abbr|b|blockquote|body|br|code|del|div|em|font|h[1-6]|head|hr|html|i|img|ins|li|mark|ol|p|pre|s|script|small|span|strike|strong|style|sub|sup|table|tbody|td|th|thead|tr|u|ul";
const HTML_ATTRIBUTES = `(?:\\s+[\\w:-]+\\s*=\\s*(?:"[^"<>]*"|'[^'<>]*'|[^\\s"'=<>\`]+))*\\s*`;
const NON_TAG_OPENING_BRACKETS = new RegExp(`<(?!\\/?(?:${HTML_TAG_NAMES})${HTML_ATTRIBUTES}\\/?>)`, "gi");
const PARKED_OPENING_BRACKET = "\uE000";
// Only a complete entity ("&amp;") is decoded: the parser would also turn the "&copy" in a plain
// URL such as "?a=1&copy=2" into "©", changing the address.
const NON_ENTITY_AMPERSANDS = /&(?!(?:[a-z][a-z0-9]*|#\d+|#x[\da-f]+);)/gi;
const PARKED_AMPERSAND = "\uE001";
// Where an HTML element ends a line or a paragraph, so that structure survives once the tags are gone.
const LINE_BREAK_TAGS = new RegExp(`<br${HTML_ATTRIBUTES}\\/?>`, "gi");
const BLOCK_TAG_NAMES = "p|div|li|h[1-6]|blockquote|tr";
// Opening tags count too, or "Hi Dana,<p>Please review</p>" would run the greeting into the paragraph.
const PARAGRAPH_START_TAGS = new RegExp(`<(?:${BLOCK_TAG_NAMES})${HTML_ATTRIBUTES}>`, "gi");
const PARAGRAPH_END_TAGS = new RegExp(`<\\/(?:${BLOCK_TAG_NAMES})\\s*>`, "gi");
const CELL_END_TAGS = /<\/t[dh]\s*>/gi;
// A list keeps its numbering or bullets, which only the markup carries. Items become lines of one
// block, so the generic <li> handling above only ever sees an item that has no list around it.
// Only a list with no list inside it matches, so lists convert from the inside out and a nested list
// is already lines, indented under its item, by the time the list around it is numbered.
const INNERMOST_LIST_ELEMENTS = new RegExp(`<(ol|ul)(${HTML_ATTRIBUTES})>((?:(?!<(?:ol|ul)\\b)[\\s\\S])*?)<\\/\\1\\s*>`, "gi");
const MAX_LIST_DEPTH = 10;
const NESTED_LIST_INDENT = "  ";
const LIST_ITEM_START_TAGS = new RegExp(`\\s*<li${HTML_ATTRIBUTES}>`, "gi");
const LIST_ITEM_END_TAGS = /<\/li\s*>/gi;
const LIST_START_ATTRIBUTE = /\bstart\s*=\s*["']?(\d+)/i;
// Elements that exist to point at a resource carry no text worth keeping, so they go, with whatever
// they wrap, before the HTML is parsed. Each leaves a marker where it stood, resolved once the tags
// around it are gone: a marker between two words becomes the one space that keeps them apart
// ("Review <img ...>this"), and one at a line edge or beside a space leaves nothing behind, so
// "See <img ...> this" keeps a single space and "Hi Dana,<img ...><br>" ends its line cleanly.
const RESOURCE_TAG_NAMES = "audio|embed|iframe|img|link|object|picture|script|source|style|video";
const RESOURCE_ELEMENTS = new RegExp(`<(${RESOURCE_TAG_NAMES})${HTML_ATTRIBUTES}>[\\s\\S]*?<\\/\\1\\s*>|<(?:${RESOURCE_TAG_NAMES})${HTML_ATTRIBUTES}\\/?>`, "gi");
const REMOVED_RESOURCE_MARKER = "\uE002";
const REMOVED_RESOURCE_RUNS = /[^\S\n]*\uE002(?:[^\S\n]*\uE002)*[^\S\n]*/g;

const FENCE_LINES = /^\s*(?:```|~~~).*$/gm;
const HEADING_MARKERS = /^\s{0,3}#{1,6}\s+/gm;
// "+" is not treated as a bullet: "+ 10% revenue" is a positive figure, and turning it into "- 10%" flips its meaning.
const BULLET_MARKERS = /^(\s*)[*-]\s+/gm;
// A markdown destination may carry a quoted title, which is not part of the address.
const LINKS_AND_IMAGES = /!?\[([^\]\n]*)\]\(([^)\s]*)(?:\s+"[^"\n]*")?\)/g;
// A leading "> " is left alone: in an email it marks a quoted reply, not a markdown blockquote.
// Emphasis has to start and end at a word edge, or snake_case, local parts of email addresses
// (first_last@example.com) and arithmetic (2*3*4) would lose their underscores and asterisks.
const STRONG_ASTERISKS = /(^|[^\w])\*\*(\S(?:[^*\n]*?\S)?)\*\*(?!\w)/g;
const EMPHASIS_ASTERISKS = /(^|[^\w])\*(\S(?:[^*\n]*?\S)?)\*(?!\w)/g;
const STRONG_UNDERSCORES = /(^|[^\w])__(\S(?:[^_\n]*?\S)?)__(?!\w)/g;
const EMPHASIS_UNDERSCORES = /(^|[^\w])_(\S(?:[^_\n]*?\S)?)_(?!\w)/g;
// A code span is literal text: "[status](true)" inside one is something the recipient should type,
// not a link, and "a*b*c" is not emphasis. The formatting still goes, since the draft is plain text,
// so each span's text sits on an indexed marker until every other pass has run. A span opened with
// several backticks closes with as many, which is how markdown writes a backtick inside a code span,
// and the single space that pads such a span on each side is part of that syntax, not of the text.
const INLINE_CODE = /(`+)([^`\n](?:[^\n]*?[^`\n])?)\1(?!`)/g;
const PADDED_CODE = /^ (.*\S.*) $/;
const PARKED_CODE = "\uE003";
const PARKED_CODE_SPANS = /\uE003(\d+)\uE003/g;

const LABEL_EDGES = /^(\s*)([\s\S]*?)(\s*)$/;

const TRAILING_SPACES = /[^\S\n]+$/gm;
const EXCESS_BLANK_LINES = /\n{3,}/g;

// A link's destination lives only in the markup, so the plain text keeps it next to the label, the
// way a mail client's "convert to plain text" does. A label that already is the address is left alone.
// Whitespace at either edge of the label stays outside the converted text: in "<a ...>the draft </a>today"
// it is what separates the link from the word after it.
function linkAsText(label: string, destination: string): string {
    const [, leading = "", text = "", trailing = ""] = LABEL_EDGES.exec(label) ?? [];
    const target = destination.trim();
    if (!target) {
        return label;
    }
    if (!text || text === target || target.replace(/^(?:https?:\/\/|mailto:|tel:)/i, "") === text) {
        return `${leading}${text || target}${trailing}`;
    }
    return `${leading}${text} (${target})${trailing}`;
}

function removedResourcesAsText(text: string): string {
    return text.replaceAll(REMOVED_RESOURCE_RUNS, (run: string, offset: number) => {
        const joinsWords = /\S/.test(text[offset - 1] ?? "") && /\S/.test(text[offset + run.length] ?? "");
        return joinsWords ? " " : "";
    });
}

function listAsText(_match: string, tagName: string, attributes: string, items: string): string {
    const ordered = tagName.toLowerCase() === "ol";
    let number = Number(LIST_START_ATTRIBUTE.exec(attributes)?.[1] ?? 1);
    const [preamble, ...entries] = items.split(LIST_ITEM_START_TAGS);
    const lines = entries.map((entry) => {
        const marker = ordered ? `${number++}. ` : "- ";
        return marker + entry.replaceAll(LIST_ITEM_END_TAGS, "").trim().replaceAll(/\n+/g, `\n${NESTED_LIST_INDENT}`);
    });
    return `\n\n${[preamble.trim(), ...lines].filter(Boolean).join("\n")}\n\n`;
}

function listsAsText(text: string): string {
    let previous = text;
    for (let depth = 0; depth < MAX_LIST_DEPTH; depth++) {
        const current = previous.replaceAll(INNERMOST_LIST_ELEMENTS, listAsText);
        if (current === previous) {
            break;
        }
        previous = current;
    }
    return previous;
}

function parkCode(parked: string[], code: string): string {
    return `${PARKED_CODE}${parked.push(code) - 1}${PARKED_CODE}`;
}

function parkMarkdownCode(text: string, parked: string[]): string {
    return text.replaceAll(INLINE_CODE, (_match, _fence: string, code: string) => parkCode(parked, PADDED_CODE.exec(code)?.[1] ?? code));
}

function unparkHtml(text: string): string {
    return removedResourcesAsText(text).replaceAll(PARKED_OPENING_BRACKET, "<").replaceAll(PARKED_AMPERSAND, "&");
}

// A DOMParser document has no browsing context, so it runs no scripts and fetches no resources, and
// reading `textContent` both drops the tags and decodes entities ("&amp;" to "&"). Resource-bearing
// elements are still removed before parsing so that nothing depends on that. DOMPurify is not used on
// purpose: it does not strip nested nodes under happy-dom, which is the environment the component
// specs run in.
function stripHtml(text: string, parkedCode: string[]): string {
    const withBreaks = listsAsText(text.replaceAll(RESOURCE_ELEMENTS, REMOVED_RESOURCE_MARKER).replaceAll(LINE_BREAK_TAGS, "\n"))
        .replaceAll(PARAGRAPH_START_TAGS, "\n\n")
        .replaceAll(PARAGRAPH_END_TAGS, "\n\n")
        .replaceAll(CELL_END_TAGS, " ")
        .replaceAll(NON_TAG_OPENING_BRACKETS, PARKED_OPENING_BRACKET)
        .replaceAll(NON_ENTITY_AMPERSANDS, PARKED_AMPERSAND);
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    // Code is parked before links so that a code span inside a link label stays literal too.
    doc.querySelectorAll("code").forEach((code) => {
        code.textContent = parkCode(parkedCode, unparkHtml(code.textContent ?? ""));
    });
    doc.querySelectorAll("a[href]").forEach((anchor) => {
        anchor.textContent = linkAsText(anchor.textContent ?? "", anchor.getAttribute("href") ?? "");
    });
    return unparkHtml(doc.body.textContent ?? "");
}

function stripMarkdown(text: string): string {
    return text
        .replaceAll(FENCE_LINES, "")
        .replaceAll(HEADING_MARKERS, "")
        .replaceAll(BULLET_MARKERS, "$1- ")
        .replaceAll(LINKS_AND_IMAGES, (_match, label: string, destination: string) => linkAsText(label, destination))
        .replaceAll(STRONG_ASTERISKS, "$1$2")
        .replaceAll(EMPHASIS_ASTERISKS, "$1$2")
        .replaceAll(STRONG_UNDERSCORES, "$1$2")
        .replaceAll(EMPHASIS_UNDERSCORES, "$1$2");
}

function unparkCode(text: string, parked: string[]): string {
    return text.replaceAll(PARKED_CODE_SPANS, (_match, index: string) => parked[Number(index)] ?? "");
}

// Turns an email draft the model wrote with stray HTML or inline markdown into the plain text a mail
// client expects, keeping every line break the draft already had. This deliberately does not go
// through `marked`: it would join the lines of a sign-off ("All the best,\nMegan") into one paragraph.
export function emailDraftPlainText(text: string): string {
    const parkedCode: string[] = [];
    const plain = stripMarkdown(stripHtml(parkMarkdownCode(text, parkedCode), parkedCode));
    return unparkCode(plain, parkedCode).replaceAll(TRAILING_SPACES, "").replaceAll(EXCESS_BLANK_LINES, "\n\n").trim();
}

// A subject is a single line whatever the model wrapped it in.
export function emailDraftPlainSubject(subject: string): string {
    return emailDraftPlainText(subject).replaceAll(/\s+/g, " ");
}
